/**
 * Builds the rows of a message timeline: day separators, a single "new" divider and message rows with
 * compact grouping. Pure — time zone and "now" are inputs.
 */
import type { ChannelKind, Message } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { messagePreview } from "./messages.ts";
import type { PendingEntry } from "./outbox.ts";
import type { RichTextContext } from "./richtext.ts";

export type SendStatus = "sent" | "sending" | "failed";

/** A day separator label: the UI renders "Today", "Yesterday" or a localized date from the parts. */
export type DayLabel =
  | { type: "today" }
  | { type: "yesterday" }
  | { type: "date"; year: number; month: number; day: number; weekday: number; showYear: boolean };

/**
 * The context line over a thread reply that was also sent to the channel ("replied to a thread: <root preview>");
 * pressing it opens the thread of `rootId`.
 */
export type ThreadBroadcast = { rootId: string; label: MessageDescriptor };

export type TimelineItem =
  | { type: "day"; key: string; label: DayLabel }
  | { type: "new"; key: string }
  | {
      type: "message";
      key: string;
      message: Message;
      grouped: boolean;
      status: SendStatus;
      /** Channel timeline: the row is a thread reply also sent to the channel (`withThreadBroadcasts`). */
      broadcast?: ThreadBroadcast;
    };

export type TimelineOptions = {
  /** Messages with seq > lastReadSeq by someone else are new; the divider goes before the first one. */
  lastReadSeq: number;
  meId: string;
  /** Minutes to add to UTC to get local time (UTC-3 → -180). Note: the opposite sign of JS getTimezoneOffset(). */
  tzOffsetMinutes: number;
  now: number;
  /** Optimistic messages, appended after `messages` in order. */
  pending?: PendingEntry[];
  /** When set, the timeline is a thread: the root message followed by its replies. No "new" divider. */
  threadRootId?: string;
};

export const GROUP_WINDOW_MS = 5 * 60 * 1000;
const DAY_MS = 86_400_000;

/** Local day number (days since 1970-01-01 in the given offset). */
export function localDay(ts: number, tzOffsetMinutes: number): number {
  return Math.floor((ts + tzOffsetMinutes * 60_000) / DAY_MS);
}

/** Civil date from a day number (Howard Hinnant's days_from_civil inverse). weekday: 0 = Sunday. */
export function civilFromDays(days: number): { year: number; month: number; day: number; weekday: number } {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36_524) - Math.floor(doe / 146_096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  const year = yoe + era * 400 + (month <= 2 ? 1 : 0);
  const weekday = (((days + 4) % 7) + 7) % 7;
  return { year, month, day, weekday };
}

export function dayLabel(day: number, today: number): DayLabel {
  if (day === today) return { type: "today" };
  if (day === today - 1) return { type: "yesterday" };
  const c = civilFromDays(day);
  const t = civilFromDays(today);
  return { type: "date", year: c.year, month: c.month, day: c.day, weekday: c.weekday, showYear: c.year !== t.year };
}

function sameAuthor(a: Message, b: Message): boolean {
  if (a.authorId !== b.authorId) return false;
  const aa = a.app === undefined ? "" : a.app.appId;
  const bb = b.app === undefined ? "" : b.app.appId;
  return aa === bb;
}

/**
 * Deleted messages drop out (like the server's history), except a thread's root: in its thread it always stays, and
 * in the channel it stays while it has replies — both as the "This message was deleted." placeholder.
 */
function inTimeline(m: Message, threadRootId: string | undefined): boolean {
  if (threadRootId !== undefined) return m.id === threadRootId || (m.threadRootId === threadRootId && !m.deleted);
  if (m.deleted && m.replyCount === 0) return false;
  return m.threadRootId === undefined || m.alsoSentToChannel === true;
}

/**
 * `messages` must be in display order (ascending seq). A message is `grouped` with the previous row when
 * both are by the same author (and app), less than 5 minutes apart, on the same day, not separated by the
 * "new" divider, and neither is deleted. In a thread, the root is never grouped with the first reply.
 */
export function buildTimeline(messages: Message[], opts: TimelineOptions): TimelineItem[] {
  const today = localDay(opts.now, opts.tzOffsetMinutes);
  const items: TimelineItem[] = [];
  let prev: Message | undefined;
  let prevDay: number | undefined;
  let dividerPlaced = opts.threadRootId !== undefined;

  const push = (m: Message, status: SendStatus, canStartNew: boolean) => {
    const day = localDay(m.createdAt, opts.tzOffsetMinutes);
    let boundary = false;
    if (day !== prevDay) {
      items.push({ type: "day", key: `day-${day}`, label: dayLabel(day, today) });
      prevDay = day;
      boundary = true;
    }
    if (!dividerPlaced && canStartNew && m.seq > opts.lastReadSeq && m.authorId !== opts.meId && !m.deleted) {
      items.push({ type: "new", key: "new" });
      dividerPlaced = true;
      boundary = true;
    }
    const isRootBoundary = opts.threadRootId !== undefined && prev !== undefined && prev.id === opts.threadRootId;
    const grouped =
      !boundary &&
      !isRootBoundary &&
      prev !== undefined &&
      !prev.deleted &&
      !m.deleted &&
      sameAuthor(prev, m) &&
      m.createdAt - prev.createdAt >= 0 &&
      m.createdAt - prev.createdAt < GROUP_WINDOW_MS;
    // My messages keep their clientId as key so the optimistic row and the server copy are the same row;
    // clientIds are only unique per author, so everyone else's rows use the message id.
    const key = m.authorId === opts.meId && m.clientId !== undefined && m.clientId !== "" ? m.clientId : m.id;
    items.push({ type: "message", key, message: m, grouped, status });
    prev = m;
  };

  for (const m of messages) {
    if (inTimeline(m, opts.threadRootId)) push(m, "sent", true);
  }
  for (const p of opts.pending ?? []) {
    if (inTimeline(p.message, opts.threadRootId)) push(p.message, p.status, false);
  }
  return items;
}

/** Messages by others after the "new" divider (for the "N new messages ↑" pill). */
export function newMessageCount(items: TimelineItem[], meId: string): number {
  let seen = false;
  let n = 0;
  for (const it of items) {
    if (it.type === "new") seen = true;
    else if (seen && it.type === "message" && it.status === "sent" && it.message.authorId !== meId) n += 1;
  }
  return n;
}

/** Longest root preview in a "replied to a thread: …" line, in characters. */
export const THREAD_PREVIEW_MAX = 80;

/**
 * The context line of a thread reply that was also sent to the channel; undefined for any other message. `root` is the
 * thread root when it is loaded: its preview follows "replied to a thread:" (none when it isn't loaded, is deleted or
 * has nothing to show).
 */
export function threadBroadcast(m: Message, root: Message | undefined, ctx: RichTextContext): ThreadBroadcast | undefined {
  const rootId = m.threadRootId;
  if (rootId === undefined || m.alsoSentToChannel !== true) return undefined;
  const preview = root === undefined || root.id !== rootId ? "" : messagePreview(root, ctx, THREAD_PREVIEW_MAX);
  return { rootId, label: preview === "" ? msg("message.repliedToThreadNoPreview") : msg("message.repliedToThread", { preview }) };
}

/**
 * Channel timeline: adds the "replied to a thread: …" context line to replies also sent to the channel (roots are
 * looked up in `messages`, the loaded history). Such a row always shows its header, so it is never grouped. Returns
 * `items` itself when nothing changes.
 */
export function withThreadBroadcasts(items: TimelineItem[], messages: Message[], ctx: RichTextContext): TimelineItem[] {
  let out: TimelineItem[] | undefined;
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    if (it.type !== "message" || it.message.threadRootId === undefined) continue;
    const rootId = it.message.threadRootId;
    const b = threadBroadcast(it.message, messages.find((m) => m.id === rootId), ctx);
    if (b === undefined) continue;
    out ??= items.slice();
    out[i] = { ...it, grouped: false, broadcast: b };
  }
  return out ?? items;
}

/**
 * A thread's labels: where it is (header: "in #design" / "with Bo Chen"), the composer's checkbox ("Also send to
 * #design") and the marker on replies that were also sent ("Also sent to #design").
 */
export type ThreadLabels = { header: MessageDescriptor; alsoSend: MessageDescriptor; alsoSent: MessageDescriptor };

/**
 * `title` = the conversation's title (the channel's name, or the DM's people). Channels name the channel with "#";
 * DMs and group DMs name the people and say "as direct message".
 */
export function threadLabels(kind: ChannelKind, title: string): ThreadLabels {
  if (kind === "dm" || kind === "group_dm") {
    return { header: msg("thread.inDm", { name: title }), alsoSend: msg("thread.alsoSendDm"), alsoSent: msg("thread.alsoSentDm") };
  }
  return {
    header: msg("thread.in", { channel: title }),
    alsoSend: msg("thread.alsoSendChannel", { channel: title }),
    alsoSent: msg("thread.alsoSentChannel", { channel: title }),
  };
}

/** Jumping to an old message (a link, search hit, pin, Activity): history pages back in pages of at most this size. */
export const REVEAL_PAGE_MAX = 200;
/** …and at least the normal page size. */
export const REVEAL_PAGE_MIN = 50;
/** Pages fetched at most while jumping (200 × 50 = the last 10 000 messages). */
export const REVEAL_MAX_PAGES = 50;

/**
 * How many older messages to load next to reach `targetSeq` when the oldest loaded one is `oldestLoadedSeq`: enough to
 * reach it with a few messages of context above (Slack shows the message in the middle), within [50, 200]. Unknown
 * target (no seq) = a normal page.
 */
export function revealPageSize(oldestLoadedSeq: number | undefined, targetSeq: number | undefined): number {
  if (oldestLoadedSeq === undefined || targetSeq === undefined) return REVEAL_PAGE_MIN;
  const need = oldestLoadedSeq - targetSeq + 10;
  return Math.min(REVEAL_PAGE_MAX, Math.max(REVEAL_PAGE_MIN, need));
}
